import type { VersionedDraftKit } from '@/lib/versioned-draft';
import type {
  AnalystFunction,
  MethodContent,
  MethodDraftContent,
  NoyauCode,
  VersionedDetail,
} from '@matter_extractor/shared';
import {
  ANALYST_FUNCTIONS,
  METHOD_FUNCTION_DESIGNATES_MAX,
  METHOD_FUNCTION_WORDS_MAX,
  METHOD_STYLE_NAME_MAX,
  METHOD_STYLE_RECOGNIZES_MAX,
  METHOD_STYLE_TYPICAL_WORDS_MAX,
  NOYAU_CODES,
  methodContentSchema,
} from '@matter_extractor/shared';

/**
 * What Réglages > Méthode holds while somebody types (analyste.md §4, issue
 * #196).
 *
 * The comparison that greys « Enregistrer », the version numbering and the
 * refusals of the pattern are the layer's (`versioned-draft.ts`). What is here is
 * the SHAPE of Méthode's draft and the functions that cross between it and the
 * wire - `VersionedDraftKit` - plus the pure rules the screen words: which field
 * is empty, which one is past its bound, and the cap that keeps an edit from
 * leaving one there.
 *
 * ## The draft IS the draft content
 *
 * Every field of Méthode is already a plain string, and the shared draft schema
 * already says the one thing a draft adds: a field may be EMPTY while somebody
 * types it. So the editor holds `MethodDraftContent` as it is, and the version is
 * what `methodContentSchema` accepts of it - the schema is asked rather than
 * re-decided, so the button of the head and the server refuse the same thing.
 */

export type MethodDraft = MethodDraftContent;

/** The two fields of a function, in the order of the card. */
export const METHOD_FUNCTION_FIELDS = ['designates', 'words'] as const;
export type MethodFunctionField = (typeof METHOD_FUNCTION_FIELDS)[number];

/** The three fields of a style, in the order of the card. */
export const METHOD_STYLE_FIELDS = ['name', 'recognizes', 'typicalWords'] as const;
export type MethodStyleField = (typeof METHOD_STYLE_FIELDS)[number];

/** The bounds of §4.2, read off `shared` and never spelled a second time. */
export const METHOD_FUNCTION_FIELD_MAX: Readonly<Record<MethodFunctionField, number>> = {
  designates: METHOD_FUNCTION_DESIGNATES_MAX,
  words: METHOD_FUNCTION_WORDS_MAX,
};
export const METHOD_STYLE_FIELD_MAX: Readonly<Record<MethodStyleField, number>> = {
  name: METHOD_STYLE_NAME_MAX,
  recognizes: METHOD_STYLE_RECOGNIZES_MAX,
  typicalWords: METHOD_STYLE_TYPICAL_WORDS_MAX,
};

/** One of the twenty-four fields of a version: a function's, or a style's. */
export type MethodFieldRef =
  | {
      readonly part: 'functions';
      readonly key: AnalystFunction;
      readonly field: MethodFunctionField;
    }
  | { readonly part: 'styles'; readonly key: NoyauCode; readonly field: MethodStyleField };

/** Why a field keeps a version from being saved. */
export type MethodProblemKind = 'empty' | 'too-long';

export interface MethodProblem {
  readonly ref: MethodFieldRef;
  readonly kind: MethodProblemKind;
}

/** Twenty-four: three functions of two fields, six styles of three (§4.2). */
export const METHOD_FIELD_TOTAL =
  ANALYST_FUNCTIONS.length * METHOD_FUNCTION_FIELDS.length +
  NOYAU_CODES.length * METHOD_STYLE_FIELDS.length;

/**
 * Does a version demand this field? Every one but the typical words of a style
 * (§4.2) - the one facultative field.
 */
export function isRequired(ref: MethodFieldRef): boolean {
  return !(ref.part === 'styles' && ref.field === 'typicalWords');
}

/** The bound of one field. */
export function maxOf(ref: MethodFieldRef): number {
  return ref.part === 'functions'
    ? METHOD_FUNCTION_FIELD_MAX[ref.field]
    : METHOD_STYLE_FIELD_MAX[ref.field];
}

/** What one field holds in a draft. */
export function fieldValue(draft: MethodDraft, ref: MethodFieldRef): string {
  return ref.part === 'functions'
    ? draft.functions[ref.key][ref.field]
    : draft.styles[ref.key][ref.field];
}

/** The characters of a field AS THE SCHEMA COUNTS THEM: the spaces of its edges are not. */
export function measureOf(text: string): number {
  return text.trim().length;
}

/**
 * What keeps ONE field from becoming a version, or null.
 *
 * Measured on the trimmed text, like `methodContentSchema` does: a field of
 * spaces alone is empty, and a bound is held by the letters and not by the
 * blanks around them - so the counter, the refusal and the server agree.
 */
export function problemOf(text: string, required: boolean, max: number): MethodProblemKind | null {
  const length = measureOf(text);
  if (length > max) return 'too-long';
  return required && length === 0 ? 'empty' : null;
}

/**
 * Every field that keeps the draft from becoming a version, in the order of the
 * screen: the three functions, then the six noyaux of the table.
 */
export function methodProblems(draft: MethodDraft): MethodProblem[] {
  const problems: MethodProblem[] = [];
  const check = (ref: MethodFieldRef) => {
    const kind = problemOf(fieldValue(draft, ref), isRequired(ref), maxOf(ref));
    if (kind !== null) problems.push({ ref, kind });
  };
  for (const key of ANALYST_FUNCTIONS) {
    for (const field of METHOD_FUNCTION_FIELDS) check({ part: 'functions', key, field });
  }
  for (const key of NOYAU_CODES) {
    for (const field of METHOD_STYLE_FIELDS) check({ part: 'styles', key, field });
  }
  return problems;
}

/**
 * A text cut at the bound of its field - what the draft keeps of what somebody typed,
 * pasted or had set by a script.
 *
 * The draft is AUTOSAVED and the draft schema keeps the bounds: a value past one
 * makes the PUT answer 400, and since the draft is sent whole, every later PUT
 * does too - the retouches of the OTHER fields never reach the server, and a
 * reload loses them. So no edit leaves a value past its bound in the draft, whatever
 * the way it came in; the controls say it to the browser as well (`maxLength`,
 * which cuts a paste at the bound), and this is the guard behind them.
 *
 * Raw length is what is cut, and that is the safe side of the schema's measure: the
 * schema trims first, so a text of `max` characters or fewer is always within it.
 * The cut never leaves HALF a surrogate pair at its end.
 */
export function cappedAt(text: string, max: number): string {
  if (text.length <= max) return text;
  const last = text.charCodeAt(max - 1);
  const splitsAPair = last >= 0xd800 && last <= 0xdbff;
  return text.slice(0, splitsAPair ? max - 1 : max);
}

/**
 * The draft with ONE field of a function rewritten - capped at its bound - and
 * everything else shared by reference.
 */
export function withFunctionField(
  draft: MethodDraft,
  key: AnalystFunction,
  field: MethodFunctionField,
  text: string,
): MethodDraft {
  const kept = cappedAt(text, METHOD_FUNCTION_FIELD_MAX[field]);
  return {
    ...draft,
    functions: { ...draft.functions, [key]: { ...draft.functions[key], [field]: kept } },
  };
}

/**
 * The draft with ONE field of a style rewritten - capped at its bound - and
 * everything else shared by reference.
 */
export function withStyleField(
  draft: MethodDraft,
  key: NoyauCode,
  field: MethodStyleField,
  text: string,
): MethodDraft {
  const kept = cappedAt(text, METHOD_STYLE_FIELD_MAX[field]);
  return {
    ...draft,
    styles: { ...draft.styles, [key]: { ...draft.styles[key], [field]: kept } },
  };
}

/**
 * Does a field differ from what the development version says?
 *
 * Compared trimmed, like the layer's own « identical » comparison: a stray space
 * is no change, and the field would otherwise wear « modifié » for a blank the
 * version never keeps.
 */
export function differsFrom(
  draft: MethodDraft,
  stored: MethodContent,
  ref: MethodFieldRef,
): boolean {
  const storedText =
    ref.part === 'functions'
      ? stored.functions[ref.key][ref.field]
      : stored.styles[ref.key][ref.field];
  return fieldValue(draft, ref).trim() !== storedText.trim();
}

/**
 * What the tab opens on: the server draft when there is one, the `développement`
 * version otherwise.
 *
 * Méthode is born at boot on the atelier from the seed of the code, so there is
 * always a version to open on; a `null` detail is a state the screen never
 * reaches - a vitrine that has not received it answers 404, and the page draws
 * its empty state without mounting an editor.
 */
export function openingDraft(
  detail: VersionedDetail<MethodContent, MethodDraftContent> | null,
): MethodDraft {
  const opened = detail?.draft?.content ?? detail?.developmentContent ?? null;
  if (opened === null) {
    throw new Error('Méthode naît d’une graine : aucune version à ouvrir.');
  }
  return opened;
}

/**
 * The version a draft would become, or `null` while a field is empty (the typical
 * words of a style excepted) or past its bound - the shared schema decides.
 */
export function normalizedContent(draft: MethodDraft): MethodContent | null {
  const parsed = methodContentSchema.safeParse(draft);
  return parsed.success ? parsed.data : null;
}

export const METHOD_DRAFT_KIT: VersionedDraftKit<MethodContent, MethodDraftContent, MethodDraft> = {
  opening: openingDraft,
  fromContent: (content) => content,
  normalized: normalizedContent,
  payload: (draft) => draft,
};
